import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit'
import { Redux } from '../..'

export type DisabledEquipmentAffectedProductDto = {
  posPlu: number
  productName: string
  sectionName: string
  menuName: string
}

export type DisabledEquipmentDetailDto = {
  id: number
  menuEquipmentId: number
  menuEquipmentName: string
  description: string | null
  isActive: boolean
  isPermanentDisable: boolean
  daysElapsed: number | null
  createdAt: string
  updatedAt: string
  tempDisableUntil: string | null
  permDisableStatus: string | null
  createdBy: number
  userName: string
  userRoleId: number
  userRoleName: string
  updatedBy: number | null
  updatedByUserName: string | null
  affectedProducts: DisabledEquipmentAffectedProductDto[]
}

export type DisabledEquipmentsByRestaurantDto = {
  restaurantId: number
  restaurantName: string
  isActive?: boolean
  isTest?: boolean
  state: string
  ownership: string
  timeZone?: string
  totalDisabledEquipments: number
  permanentDisabledCount: number
  temporaryDisabledCount: number
  averageDaysDisabled: number | null
  maxDaysDisabled: number | null
  equipments: DisabledEquipmentDetailDto[]
  assignedUsers?: { userId: number; name: string; roleId: number; roleName: string; roleHandle: string }[]
}

type DisabledEquipmentsState = {
  loading: boolean
  data: DisabledEquipmentsByRestaurantDto[]
  count: number
  error: SerializedError | null
}

type State = {
  disabledEquipments: DisabledEquipmentsState
}

const initialState: State = {
  disabledEquipments: {
    loading: false,
    data: [],
    count: 0,
    error: null
  }
}

type fetchDisabledEquipmentsParams = {
  countryId: number
}

export const fetchDisabledEquipments = createAsyncThunk<
  DisabledEquipmentsByRestaurantDto[] | null,
  fetchDisabledEquipmentsParams,
  Redux
>('api/fetchDisabledEquipments', async (fetchDisabledEquipmentsParams, { extra }) => {
  try {
    const response = await extra.httpClient(
      `resto365-restaurant-menu-equipments/disabled-equipments/${fetchDisabledEquipmentsParams.countryId}`
    )

    const {
      data: { data: disabledEquipments }
    } = response

    return disabledEquipments as DisabledEquipmentsByRestaurantDto[]
  } catch (error) {
    throw new Error('Failed to fetch Disabled Equipments')
  }
})

const disabledEquipmentsReducer = createSlice({
  name: 'disabledEquipments',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchDisabledEquipments.pending, state => {
        state.disabledEquipments.loading = true
        state.disabledEquipments.error = null
      })
      .addCase(fetchDisabledEquipments.fulfilled, (state, action) => {
        state.disabledEquipments.loading = false
        state.disabledEquipments.data = action.payload || []
        state.disabledEquipments.count = action.payload?.length || 0
      })
      .addCase(fetchDisabledEquipments.rejected, (state, action) => {
        state.disabledEquipments.loading = false
        state.disabledEquipments.error = action.error || null
      })
  }
})

export default disabledEquipmentsReducer.reducer
